import { OpenInNew } from '@mui/icons-material';
import { Grid, IconButton, MenuItem, Paper, Select, Skeleton, TextField } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import { Activity } from '@thro-types/activity-log';

import { useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useActivitiesQuery, useActivityApplicationsQuery } from 'api/hooks/useActivityLog';
import { TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { formatToDateAndTime } from 'utils/dates';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

export const ActivityList = () => {
  const [search, setSearch] = useQueryState('search', { defaultValue: '' });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const currentURL = useCurrentURL();

  const [application, setApplication] = useQueryState('application', { defaultValue: '' });
  const navigate = useNavigate();
  const [sortingmodel, setSortingModel] = useState<GridSortModel>([
    { field: 'timestamp', sort: 'desc' }
  ]);
  const { user } = useAuthContext();
  const isSuperUser = new ACLHelper(user).isSuperUser();

  const { t } = useTranslation();

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortingModel(model);
  }, []);

  usePageHeading({ heading: t(translations.pages.activityLog.title()), mobilePadding: false });

  const { data: applications, isLoading: isLoadingApplications } = useActivityApplicationsQuery();

  const { data: activitiesData, isLoading } = useActivitiesQuery(!!application, {
    params: {
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      sort: sortingmodel[0]?.field,
      sortOrder: sortingmodel[0]?.sort,
      text: search,
      application
    }
  });

  const columns = useMemo((): TypedGridColumns<Activity> => {
    return [
      {
        field: 'action',
        renderHeader: () => null,
        disableColumnMenu: true,
        sortable: false,
        flex: 0.5,
        align: 'center',
        renderCell: params =>
          params.row.action.name === 'update' &&
          isSuperUser && (
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/activity-log/${params.row.id}`, { state: { back: currentURL } })
              }
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          )
      },
      {
        field: 'id',
        flex: 1,
        headerName: t(translations.common.id()),
        align: 'center'
      },
      {
        field: 'timestamp',
        flex: 2,
        headerName: t(translations.pages.activityLog.columns.time()),
        renderCell: params => formatToDateAndTime(params.value)
      },
      {
        field: 'domain',
        flex: 1.5,
        headerName: t(translations.pages.activityLog.columns.domain()),
        renderCell: params => params.row.domain.name
      },
      {
        field: 'description',
        flex: 5,
        headerName: t(translations.pages.activityLog.columns.description())
      },
      {
        field: 'initiatorType',
        flex: 1,
        headerName: t(translations.pages.activityLog.columns.type())
      },
      {
        field: 'initiatorId',
        flex: 1,
        headerName: t(translations.pages.activityLog.columns.refId())
      },
      {
        field: 'action.name',
        flex: 1,
        headerName: t(translations.pages.activityLog.columns.action()),
        renderCell: params => params.row.action.name
      }
    ];
  }, [t, navigate, currentURL, isSuperUser]);

  useEffect(() => {
    if (applications && applications.length === 1) {
      setApplication(applications[0].name);
    }
  }, [applications, setApplication]);

  if (isLoadingApplications) {
    return <Skeleton width={200} height={40} sx={{ transform: 'none' }} />;
  }

  if (!applications?.length && !isLoading && !isLoadingApplications) {
    return t(translations.common.noPermission());
  }

  return (
    <Grid container justifyContent="flex-start" gap={1}>
      {!!application && (
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t(translations.common.search())}
          size="small"
        />
      )}
      {applications && applications.length > 0 && (
        <Select
          autoFocus
          value={application}
          onChange={e => setApplication(e.target.value)}
          slotProps={{ input: { placeholder: t(translations.common.search()) } }}
          size="small"
          style={{ width: 200 }}
        >
          {applications?.map(application => (
            <MenuItem value={application.name}>
              {t(translations.pages.activityLog.applications[application.name]())}
            </MenuItem>
          ))}
        </Select>
      )}
      {!!application && (
        <Grid container component={Paper} style={{ height: '80vh' }}>
          <DataGrid
            columns={columns}
            rows={activitiesData?.data || []}
            style={{ height: '100%', width: '100%' }}
            paginationMode="server"
            paginationModel={{
              page: paginationModel.page,
              pageSize: paginationModel.pageSize
            }}
            rowCount={activitiesData?.pagination.count || 0}
            onPaginationModelChange={pagModel => {
              handlePaginationChange(pagModel);
            }}
            columnVisibilityModel={{ id: false }}
            sortingMode="server"
            filterMode="server"
            onSortModelChange={handleSortModelChange}
            loading={isLoading}
          />
        </Grid>
      )}
    </Grid>
  );
};
